import React, { useCallback, useEffect, useState, useRef } from "react";
import Layout1 from "../../DataLayouts/Layout1";
import NextWeekIcon from "@mui/icons-material/NextWeek";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import { MAIN_URL } from "../../../Configurations/Urls";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import Layout4 from "../../DataLayouts/Layout4";
import { useNavigate, useSearchParams } from "react-router-dom";
import { operatorRequiresValue } from "../../../Components/filters/operators";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_language_id",
  sortOrder: "desc",
};

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  const search = searchParams.get("search");
  if (search) {
    filters.push({
      field: "language_name",
      operator: "contains",
      value: search,
    });
  }

  return filters;
};

function OrganizationLanguageList() {
  const [Languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  // Load data
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "organization_language_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
        };

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/languages-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data = [];
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const b = data.map((item) => {
          return {
            ...item,
            id: item.organization_language_id,
          };
        });

        setLanguages(b);
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }
        console.error("Error fetching languages:", error);
        toast.error("Failed to load languages");
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org?.organization_id, searchParams]);

  const handleDelete = useCallback(
    async (id) => {
      try {
        const org_id = org.organization_id;
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org_id}/languages/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          toast.success(result.message || "Language deleted successfully");
          const params = new URLSearchParams(searchParams);
          setSearchParams(params);
        } else {
          const errorData = await response.json();
          if (response.status === 401) {
            toast.error("Session Expired!");
            window.location.href = "/login";
          } else {
            const errorMessage =
              errorData.message ||
              errorData.errors?.[Object.keys(errorData.errors || {})[0]]?.[0] ||
              "Failed to delete Language";
            toast.error(errorMessage);
          }
        }
      } catch (error) {
        console.error("Delete failed:", error);
        toast.error("Failed to delete Language");
      }
    },
    [org?.organization_id, searchParams, setSearchParams]
  );

  const handleEdit = useCallback(

    (item) => {

         navigate(`/organization-configration/languages/edit/${item.id}`);

    },

    [navigate]

  );



                        const handleShow = useCallback(
      (item) => {
        navigate(`/organization-configration/languages/view/${item.id}`)
      },
      [navigate],
    )




  return (
    <>
      <Layout4
        loading={loading}
        heading={"Languages"}
        btnName={"Add Languages"}
        Data={Languages}
        delete_action={"LANGUAGE_DELETE"}
        add_action={"LANGUAGE_ADD"}
        tableHeaders={[
          {
            name: "Language Code",
            value_key: "language_code",
            width: "300px",
            textStyle: "capitalize",
          },
          {
            name: "Language ",
            value_key: "language_name",
            width: "300px",
            textStyle: "capitalize",
          },
          { name: "Description", value_key: "description", width: "400px" },
        ]}
        Icons={[
          <FormatAlignJustifyIcon
            sx={{ fontSize: 60, color: "grey.500", mb: 2 }}
          />,
          <FormatAlignJustifyIcon color="primary" />,
          <NextWeekIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={["Languages", "Languages", "Add Languages", "Languages"]}
        Route={"/organization-configration/languages"}
        setData={setLanguages}
        DeleteFunc={handleDelete}
      />




      
            <TableDataGeneric
              tableName="organization_languages"
              primaryKey="organization_language_id"
              heading="Languages"
              data={Languages}
              sortname="language_name"
              showActions={true}
              Route="/organization-configration/languages"
              DeleteFunc={handleDelete}
              EditFunc={handleEdit}
              handleShow={handleShow}
              token={localStorage.getItem("token")}
              edit_delete_action={["LANGUAGE_DELETE", "LANGUAGE_EDIT"]}
              organizationUserId={userData?.organization_user_id}
              showLayoutButtons={true}
              paginationData={paginationData}
              filterModule="organization_languages"
              config={{
                defaultVisibleColumns: ["language_code","language_name","description"],
                mandatoryColumns: ["language_code","language_name","description"],
              }}
            />
      
    </>
  );
}

export default OrganizationLanguageList;
